import { DollarSign, Bitcoin } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { dispatchGAEvent } from 'utils/googleAnalytics';
import { logEventToHotjar } from 'utils/logging';

import {
  NotificationContainer,
  type NotificationInfo
} from '@organisms/Notifications';
import { DrilldownTables } from '@templates/DrilldownTables/DrilldownTables';
import { useGetCustomerConfigurations } from 'api';
import { DashboardAccountWidgets } from 'containers/DashboardAccountWidgets';
import { Restricted } from 'containers/Restricted';

import { useContractedProducts, useOrganisationId } from 'hooks/context';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';
import { useGlobalFilters } from 'hooks/useGlobalFilters';
import { useIsCryptoFundingEnabled } from 'hooks/useIsCryptoFundingEnabled';
import { useRedirectTo } from 'hooks/useRedirectTo';

import { WidgetPayoutStatusesContainer } from './StatusWidget/containers/WidgetPayoutStatusesContainer';
import { WidgetOrderStatusesContainer } from './StatusWidget/containers/WidgetsOrderStatusesContainer';
import { FXWidget } from './WidgetFXRate/FXWidget';

const DASHBOARD_DATE_RANGE = 'd30';
const NOTIFICATION_CRYPTO_FUNDING = 'notifications:crypto_funding';
const NOTIFICATION_NEW_CREATE_MANUAL_PAYOUT =
  'notifications:new_create_manual_payout';

export const Dashboard = () => {
  const { setDateRange, filters } = useGlobalFilters();
  const organisationId = useOrganisationId<number>();
  const { contractedProducts } = useContractedProducts();

  const { data: { data: orgSettings } = {} } =
    useGetCustomerConfigurations(organisationId);

  const hasPayoutProducts = Boolean(
    contractedProducts?.meta?.payout_products_count
  );

  const configuration = useFeatureAccessControl();

  const hasCollectionProducts = Boolean(
    contractedProducts?.meta?.collection_products_count
  );

  useEffect(() => {
    logEventToHotjar('dashboard:page-open');

    setDateRange({
      type: DASHBOARD_DATE_RANGE
    });
  }, [setDateRange]);

  const { navigateWithRedirectTo } = useRedirectTo();

  const isCryptoFundingEnabled = useIsCryptoFundingEnabled();

  const isDateRangeUpdated = filters.dateRange.type === DASHBOARD_DATE_RANGE;
  const isExchangeRatesEnabled = orgSettings?.fx_widget_enabled;

  const { notifications, setNotification } = useLocalStorageKeyValues([
    {
      my_key: NOTIFICATION_CRYPTO_FUNDING,
      value: { closed: false }
    },
    {
      my_key: NOTIFICATION_NEW_CREATE_MANUAL_PAYOUT,
      value: { closed: false }
    }
  ]);

  const shouldShowCryptoBanner = useMemo(() => {
    return (
      isCryptoFundingEnabled &&
      notifications.find(
        (notification) => notification.my_key === NOTIFICATION_CRYPTO_FUNDING
      )?.value.closed === false
    );
  }, [isCryptoFundingEnabled, notifications]);

  const shouldShowNewManualPayoutBanner = useMemo(() => {
    return (
      configuration.isCustomerABTestingEnabled &&
      notifications.find(
        (notification) =>
          notification.my_key === NOTIFICATION_NEW_CREATE_MANUAL_PAYOUT
      )?.value.closed === false
    );
  }, [configuration.isCustomerABTestingEnabled, notifications]);

  return (
    <>
      {isDateRangeUpdated && (
        <div
          role="tabpanel"
          className="flex size-full flex-col gap-4 overflow-auto p-4"
        >
          {shouldShowCryptoBanner && (
            <NotificationContainer
              message="Crypto funding now available"
              actionMessage="Add crypto funds"
              title="Crypto Funding"
              icon={
                <div className="rounded-full bg-black p-2">
                  <Bitcoin
                    strokeWidth={2}
                    stroke="var(--tenant-action-secondary)"
                    size={32}
                  />
                </div>
              }
              onAction={() => {
                setNotification(NOTIFICATION_CRYPTO_FUNDING, {
                  closed: true
                });
                navigateWithRedirectTo(`/org/${organisationId}/crypto_funding`);
              }}
              onClose={() => {
                setNotification(NOTIFICATION_CRYPTO_FUNDING, {
                  closed: true
                });
              }}
            />
          )}

          {shouldShowNewManualPayoutBanner && (
            <NotificationContainer
              title="New manual payout flow"
              message="Live summary, helpful tooltips, and step-by-step guidance for effortless payout creation"
              actionMessage="Try it now"
              icon={
                <div className="rounded-full bg-black p-2">
                  <DollarSign
                    strokeWidth={2}
                    stroke="var(--tenant-action-secondary)"
                    size={32}
                  />
                </div>
              }
              onAction={() => {
                setNotification(NOTIFICATION_NEW_CREATE_MANUAL_PAYOUT, {
                  closed: true
                });
                logEventToHotjar('new-manual-payout:intent');
                dispatchGAEvent('create_manual_payout_intent', {
                  version: 'v2'
                });
                navigateWithRedirectTo(`/org/${organisationId}/payouts/create`);
              }}
              onClose={() => {
                setNotification(NOTIFICATION_NEW_CREATE_MANUAL_PAYOUT, {
                  closed: true
                });
              }}
            />
          )}

          <div className="dashboard flex w-full justify-between gap-8">
            <div className="flex max-w-[960px] grow flex-col gap-4">
              <Restricted requiredPermission="accounts:read">
                <DashboardAccountWidgets />
              </Restricted>
              {isExchangeRatesEnabled ? <FXWidget /> : null}
              {hasPayoutProducts ? <DrilldownTables /> : null}
            </div>
            <div className="flex min-w-64 flex-col gap-4">
              {hasPayoutProducts ? <WidgetPayoutStatusesContainer /> : null}
              {hasCollectionProducts ? <WidgetOrderStatusesContainer /> : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function useLocalStorageKeyValues<
  T extends { my_key: string; value: NotificationInfo }
>(defaultValues: Array<T>) {
  const [notifications, setNotifications] = useLocalStorage<T[]>(
    'notifications',
    defaultValues
  );

  function setNotification(key: string, value: NotificationInfo) {
    setNotifications(
      notifications.map((item) => {
        if (item.my_key === key) {
          item.value = value;
        }
        return item;
      })
    );
  }

  return { notifications, setNotification };
}
